<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">商品管理</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-primary" @click="handleCreate">
          <i class="ri-add-line"></i>
          新增商品
        </button>
      </div>
    </div>

    <div class="page-container">
    <!-- 進銷存狀態提示 -->
    <div v-if="inventoryEnabled" class="inventory-banner inventory-banner--enabled">
      <i class="ri-store-3-line"></i>
      <span>已啟用<strong>進銷存管理</strong>，商品庫存由分倉統一管理。</span>
      <div class="inventory-banner-actions">
        <router-link to="/client/inventory/overview" class="banner-link">
          <i class="ri-bar-chart-box-line"></i> 庫存總覽
        </router-link>
        <router-link to="/client/inventory-settings" class="banner-link">
          <i class="ri-settings-3-line"></i> 進銷存設定
        </router-link>
      </div>
    </div>
    <div v-else class="inventory-banner inventory-banner--disabled">
      <i class="ri-information-line"></i>
      <span>目前<strong>尚未啟用進銷存模組</strong>。商品庫存欄位可手動輸入管理；如需分倉庫存、進貨、調撥等功能，請聯繫平台客服申請開通。</span>
    </div>

    <div class="page-toolbar">
      <div class="search-box">
        <input
          v-model="searchQuery"
          type="text"
          class="form-control"
          placeholder="搜尋商品名稱、SKU、條碼..."
          @input="handleSearch"
        />
        <i class="ri-search-line"></i>
      </div>
      <div class="filter-group">
        <select v-model="filterStatus" class="form-control" @change="handleFilterChange">
          <option value="">全部狀態</option>
          <option value="active">上架</option>
          <option value="inactive">下架</option>
        </select>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchProducts">重試</button>
    </div>

    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>圖片</th>
            <th>商品名稱</th>
            <th>分類</th>
            <th>SKU</th>
            <th>條碼</th>
            <th>狀態</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!products || products.length === 0">
            <td colspan="7" class="empty-state">
              沒有找到商品
            </td>
          </tr>
          <tr v-for="product in products" :key="product.id">
            <td>
              <img
                v-if="product.imageUrl"
                :src="product.imageUrl"
                alt=""
                class="product-thumb"
              />
              <span v-else class="text-muted">-</span>
            </td>
            <td>
              {{ product.name || '-' }}
              <span v-if="product.productType === 'bundle'" class="badge badge-bundle">套組</span>
            </td>
            <td>
              <span v-if="product.categoryParentName" class="cat-breadcrumb">
                {{ product.categoryParentName }} / {{ product.categoryName }}
              </span>
              <span v-else-if="product.categoryName" class="cat-breadcrumb">
                {{ product.categoryName }}
              </span>
              <span v-else class="text-muted">-</span>
            </td>
            <td>{{ product.sku || '-' }}</td>
            <td>{{ product.barcode || '-' }}</td>
            <td>
              <span :class="product.status === 'active' ? 'badge badge-success' : 'badge badge-secondary'">
                {{ product.status === 'active' ? '上架' : '下架' }}
              </span>
            </td>
            <td>
              <div class="action-buttons">
                <button
                  class="btn btn-sm btn-secondary"
                  @click="handleEdit(product)"
                  title="編輯"
                >
                  <i class="ri-edit-line"></i>
                </button>
                <button
                  class="btn btn-sm btn-danger"
                  @click="handleDelete(product)"
                  title="刪除"
                >
                  <i class="ri-delete-bin-line"></i>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <ListPagination
      v-if="total > 0"
      :page="currentPage"
      :total-pages="totalPages"
      :total="total"
      @change="goToPage"
    />
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { productService } from '@/api/modules/products.js';
import { showError, showDeleteConfirm, showSuccess } from '@/utils/swal.js';
import { useMenuStore } from '@/stores/menu.js';

export default {
  name: 'ClientProducts',
  setup() {
    const router = useRouter();
    const menuStore = useMenuStore();

    const inventoryEnabled = computed(() =>
      menuStore.menuItems.some(m => m.code === 'INVENTORY')
    );

    const products = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const searchQuery = ref('');
    const filterStatus = ref('');
    const currentPage = ref(1);
    const pageSize = ref(20);
    const total = ref(0);
    const searchTimeout = ref(null);

    const totalPages = computed(() => Math.ceil(total.value / pageSize.value));

    const fetchProducts = async () => {
      loading.value = true;
      error.value = null;
      try {
        const params = {
          page: currentPage.value,
          pageSize: pageSize.value,
        };
        if (searchQuery.value) params.search = searchQuery.value;
        if (filterStatus.value) params.status = filterStatus.value;

        const response = await productService.getProducts(params);
        if (response && response.success && response.data) {
          products.value = response.data.items || [];
          total.value = response.data.pagination?.totalItems || 0;
        } else {
          error.value = (response && response.message) || '獲取商品列表失敗';
        }
      } catch (err) {
        console.error('獲取商品列表失敗:', err);
        error.value = err.message || err.response?.data?.message || '獲取商品列表失敗';
        await showError('載入失敗', error.value);
      } finally {
        loading.value = false;
      }
    };

    const handleSearch = () => {
      if (searchTimeout.value) clearTimeout(searchTimeout.value);
      searchTimeout.value = setTimeout(() => {
        currentPage.value = 1;
        fetchProducts();
      }, 500);
    };

    const handleFilterChange = () => {
      currentPage.value = 1;
      fetchProducts();
    };

    const goToPage = (page) => {
      if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page;
        fetchProducts();
      }
    };

    const handleCreate = () => {
      router.push('/client/products/create');
    };

    const handleEdit = (product) => {
      router.push(`/client/products/${product.id}/edit`);
    };

    const handleDelete = async (product) => {
      const isBundle = product.productType === 'bundle';

      // 套組商品：告知只會移除套組定義，本體保留
      // 一般商品：若啟用進銷存則強調庫存也會清除
      let customMessage = null;
      let confirmTitle = '確定要刪除嗎？';
      if (isBundle) {
        confirmTitle = '確定要解除套組嗎？';
        customMessage = `套組定義（所含商品組合）將被移除，\n商品「${product.name}」本身的基本資料與規格將保留，不會被刪除。`;
      } else if (inventoryEnabled.value) {
        customMessage = [
          '商品基本資料與所有規格',
          '各倉庫的庫存數量',
          '庫存異動紀錄（進貨、調撥、扣減記錄）',
          '歷史訂單的商品連結（訂單本身保留，商品名稱快照不受影響）',
        ];
      } else {
        customMessage = '確定要刪除此商品嗎？歷史訂單本身不受影響（訂單建立時已儲存商品名稱快照），但商品資料將無法復原。';
      }

      const confirmed = await showDeleteConfirm(product.name || '此商品', customMessage);
      if (!confirmed) return;
      try {
        const res = await productService.deleteProduct(product.id);
        const action = res?.data?.action;
        if (action === 'unbundled') {
          await showSuccess('套組已解除', `商品「${product.name}」的套組定義已移除，商品本體保留`);
        } else {
          await showSuccess('已刪除', `商品「${product.name}」已成功刪除`);
        }
        await fetchProducts();
      } catch (err) {
        const msg = err.response?.data?.message || err.message;
        await showError('無法刪除', msg);
      }
    };

    onMounted(() => {
      fetchProducts();
    });

    return {
      products,
      inventoryEnabled,
      loading,
      error,
      searchQuery,
      filterStatus,
      currentPage,
      totalPages,
      total,
      handleSearch,
      handleFilterChange,
      goToPage,
      handleCreate,
      handleEdit,
      handleDelete,
      fetchProducts,
    };
  },
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.table-wrapper table {
  min-width: 800px;
}

/* ── 進銷存狀態橫幅 ── */
.inventory-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 6px;
  font-size: 13px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.inventory-banner i {
  font-size: 16px;
  flex-shrink: 0;
}

.inventory-banner--enabled {
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #1e40af;
}

.inventory-banner--enabled i {
  color: #2563eb;
}

.inventory-banner--disabled {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  color: #6b7280;
}

.inventory-banner--disabled i {
  color: #9ca3af;
}

.inventory-banner-actions {
  display: flex;
  gap: 12px;
  margin-left: auto;
  flex-shrink: 0;
}

.banner-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 500;
  color: #2563eb;
  text-decoration: none;
  padding: 3px 8px;
  border: 1px solid #bfdbfe;
  border-radius: 4px;
  background: #fff;
  transition: background 0.15s;
}

.banner-link:hover {
  background: #dbeafe;
}

@media (max-width: 640px) {
  .inventory-banner-actions {
    margin-left: 0;
    width: 100%;
  }
}

.product-thumb {
  width: 64px;
  height: 64px;
  object-fit: cover;
  border-radius: 4px;
}

.badge-bundle {
  display: inline-block;
  font-size: 10px;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 10px;
  margin-left: 6px;
  background: #ede9fe;
  color: #6d28d9;
  vertical-align: middle;
}

.cat-breadcrumb {
  font-size: 14px;
  color: #6b7280;
  white-space: nowrap;
}
</style>
